'use client';

import { Button, Row, Stack } from '@slims/ui/atoms';
import { PanelCard, ToggleRow } from '@slims/ui/molecules';
import { Save } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Field, SelectInput } from '@/components/admin/detail-fields';
import { useContextOrg } from '@/hooks/use-context-org';
import { useCERStore } from '@/stores/cer-store';
import { apiFetch } from '@/lib/utils/api';
import {
	ALERT_CONSUMI_KEY,
	ALERT_ML_ACCURACY_KEY,
	ALERT_POD_KEY,
	alertEnabled,
	ML_ACCURACY_THRESHOLD_KEY,
	mlAccuracyThreshold,
	WEEKDAY_LABELS_IT,
	WEEKLY_SUMMARY_DAY_KEY,
	WEEKLY_SUMMARY_ENABLED_KEY,
	weeklySummaryDay,
} from '@slims/shared/constants';

/**
 * Preferenze della configurazione che vivevano nella modale "Impostazioni" della
 * barra superiore. Ora stanno nel profilo utente, come nell'app mobile (INB-107).
 * Le tab Config CER, Impianto e Parametri Eco. non sono state riportate: quei dati
 * si modificano in Configurazioni → Dettagli.
 */

/** Giorni selezionabili per l'invio del riepilogo settimanale (ISO 1–7). */
const WEEKDAY_OPTIONS = Object.entries(WEEKDAY_LABELS_IT).map(([value, label]) => ({
	value,
	label,
}));

/**
 * Soglie selezionabili per l'alert di accuratezza. A scalini invece che a campo
 * libero: il numero esatto non cambia nulla (85 e 86 sono la stessa decisione) e
 * un campo aperto invita a valori che l'alert non può onorare, come 99%.
 */
const ACCURACY_THRESHOLD_OPTIONS = [60, 70, 75, 80, 85, 90].map((pct) => ({
	value: String(pct),
	label: `${pct}%`,
}));

/**
 * Interruttori delle notifiche automatiche. Le chiavi restano quelle già salvate
 * in `configurations.settings`, così le preferenze esistenti non si perdono:
 * `anomalyDetection` e `ottimizzazioneAI` cambiano solo etichetta.
 */
const NOTIF_TOGGLES: { key: string; label: string; sub: string }[] = [
	{
		key: ALERT_POD_KEY,
		label: 'Alert anomalie POD',
		sub: 'Codici POD errati o duplicati, anagrafica incompleta, misure che non arrivano',
	},
	{
		key: ALERT_CONSUMI_KEY,
		label: 'Alert irregolarità consumi',
		sub: 'Picchi anomali, consumo a zero prolungato e crolli rispetto al mese precedente',
	},
	{
		key: WEEKLY_SUMMARY_ENABLED_KEY,
		label: 'Email riepilogo settimanale',
		sub: 'Panoramica produzione, consumi e autoconsumo',
	},
	{
		key: 'reportMensile',
		label: 'Report mensile',
		sub: 'Genera e invia estratti conto a ogni socio',
	},
	{
		key: 'ottimizzazioneAI',
		label: 'Ottimizzazione consumi',
		sub: 'Sposta carichi flessibili verso i picchi FV',
	},
	{
		key: 'manutenzionePredittiva',
		label: 'Manutenzione predittiva',
		sub: 'Alert su anomalie e manutenzione degli impianti',
	},
	{
		key: ALERT_ML_ACCURACY_KEY,
		label: 'Alert accuratezza previsioni',
		sub: 'Avvisa quando il modello predittivo scende sotto la soglia di accuratezza',
	},
	{
		key: 'previsioneAI',
		label: 'Previsione AI 7 giorni',
		sub: 'Abilita la pagina Previsione AI (ML su 14 variabili meteo)',
	},
];

/** Legge `configurations.settings` come mappa chiave → valore. */
function useNotifSettings() {
	const config = useCERStore((s) => s.config);
	return useMemo(() => {
		try {
			return JSON.parse(config?.settings ?? '{}') as Record<string, boolean | number>;
		} catch {
			return {} as Record<string, boolean | number>;
		}
	}, [config?.settings]);
}

/** PATCH della configurazione corrente; ritorna true se il salvataggio è andato a buon fine. */
function useSaveConfig() {
	const config = useCERStore((s) => s.config);
	const setConfig = useCERStore((s) => s.setConfig);
	const contextOrg = useContextOrg();
	const [saving, setSaving] = useState(false);

	async function save(patch: Record<string, unknown>) {
		if (!contextOrg) {
			toast.error('Nessuna configurazione selezionata');
			return false;
		}
		setSaving(true);
		try {
			const { membersCount: _mc, ...current } = config;
			const res = await apiFetch(`/api/cer?orgId=${contextOrg.orgId}`, {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ ...current, ...patch }),
			});
			if (!res.ok) {
				const err = (await res.json().catch(() => null)) as { error?: string } | null;
				toast.error(err?.error ?? `Errore nel salvataggio (${res.status})`);
				return false;
			}
			setConfig(await res.json());
			return true;
		} catch {
			toast.error('Errore di rete');
			return false;
		} finally {
			setSaving(false);
		}
	}

	return { save, saving };
}

/** Card "Notifiche automatiche" del profilo — visibile solo agli amministratori. */
export function NotificationPreferencesCard() {
	const notifSettings = useNotifSettings();
	const { save, saving } = useSaveConfig();
	const [draft, setDraft] = useState<Record<string, boolean | number> | null>(null);
	const current = draft ?? notifSettings;

	// Un'impostazione assente vale "attiva": il default di ogni notifica è on
	// (INB-109 — va usato anche in `toggle`, o il primo click non muove la levetta).
	// `alertEnabled` è lo stesso predicato che applica il cron dell'API (INB-24):
	// se le due superfici leggessero il default in modo diverso, la levetta
	// mostrerebbe uno stato che il cron non rispetta.
	const isOn = (key: string) => alertEnabled(current, key);

	function toggle(key: string) {
		setDraft({ ...current, [key]: !isOn(key) });
	}

	function setSendDay(value: string) {
		setDraft({ ...current, [WEEKLY_SUMMARY_DAY_KEY]: Number(value) });
	}

	function setAccuracyThreshold(value: string) {
		setDraft({ ...current, [ML_ACCURACY_THRESHOLD_KEY]: Number(value) });
	}

	async function handleSave() {
		if (!draft) return;
		if (await save({ settings: JSON.stringify(draft) })) {
			setDraft(null);
			toast.success('Preferenze notifiche salvate');
		}
	}

	return (
		<PanelCard title="Notifiche automatiche">
			<Stack gap="sm">
				<div>
					{NOTIF_TOGGLES.map((t) => (
						<ToggleRow
							key={t.key}
							label={t.label}
							sub={t.sub}
							checked={isOn(t.key)}
							onChange={() => toggle(t.key)}
						/>
					))}
				</div>
				{/* Il giorno di invio ha senso solo a riepilogo acceso: spento, il
				    campo chiederebbe di configurare un'email che non parte. */}
				{isOn(WEEKLY_SUMMARY_ENABLED_KEY) && (
					<Field
						label="Giorno di invio del riepilogo"
						hint="L'email copre i sette giorni precedenti all'invio"
					>
						<SelectInput
							value={String(weeklySummaryDay(current))}
							onChange={setSendDay}
							options={WEEKDAY_OPTIONS}
						/>
					</Field>
				)}
				{/* Stessa logica del giorno di invio: la soglia si configura solo se
				    l'alert che la usa è acceso. */}
				{isOn(ALERT_ML_ACCURACY_KEY) && (
					<Field
						label="Soglia di accuratezza previsioni"
						hint="Sotto questa percentuale il cron giornaliero notifica il degrado del modello (default 80%)"
					>
						<SelectInput
							value={String(mlAccuracyThreshold(current))}
							onChange={setAccuracyThreshold}
							options={ACCURACY_THRESHOLD_OPTIONS}
						/>
					</Field>
				)}
				<Row justify="end">
					<Button
						type="button"
						variant="accent"
						size="sm"
						onClick={handleSave}
						disabled={!draft || saving}
					>
						<Save />
						{saving ? 'Salvataggio…' : 'Salva preferenze'}
					</Button>
				</Row>
			</Stack>
		</PanelCard>
	);
}

